import { useEffect, useState } from "react";
import { Brain, Trophy } from "@phosphor-icons/react";
import { endpoints } from "../lib/api";

const fmt = (n, d = 2) =>
  Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });

const SourceCard = ({ testid, label, accent, stat, winner }) => {
  const pnl = stat?.total_pnl ?? 0;
  const hasTrades = (stat?.trades ?? 0) > 0;
  return (
    <div
      data-testid={testid}
      className={`border rounded-sm p-4 relative ${
        winner ? `${accent.border} ${accent.bg}` : "border-border bg-[#0a0a0a]"
      }`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Brain size={14} weight="fill" className={accent.text} />
          <span className="font-heading font-black text-sm">{label}</span>
        </div>
        {winner && (
          <span className={`flex items-center gap-1 font-mono text-[9px] uppercase px-1.5 py-0.5 rounded-sm border ${accent.border} ${accent.text} ${accent.bg}`}>
            <Trophy size={10} weight="fill" /> Unggul
          </span>
        )}
      </div>
      {hasTrades ? (
        <div className="space-y-2 font-mono text-[11px]">
          <div className={`font-bold text-2xl ${pnl >= 0 ? "text-signal-buy" : "text-signal-sell"}`} data-testid={`${testid}-pnl`}>
            {pnl >= 0 ? "+" : ""}${fmt(pnl)}
          </div>
          <div className="flex items-center justify-between border-b border-border pb-1">
            <span className="text-muted-foreground uppercase">Win Rate</span>
            <span className="text-white">{stat.win_rate}%</span>
          </div>
          <div className="flex items-center justify-between border-b border-border pb-1">
            <span className="text-muted-foreground uppercase">Trades</span>
            <span className="text-white">
              {stat.trades} <span className="text-signal-buy">({stat.wins}W</span>/<span className="text-signal-sell">{stat.losses}L)</span>
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground uppercase">Avg PnL</span>
            <span className={stat.avg_pnl >= 0 ? "text-signal-buy" : "text-signal-sell"}>
              {stat.avg_pnl >= 0 ? "+" : ""}${fmt(stat.avg_pnl)}
            </span>
          </div>
        </div>
      ) : (
        <div className="font-mono text-[11px] text-muted-foreground py-4">
          Belum ada trade dari sumber ini
        </div>
      )}
    </div>
  );
};

export default function AIComparisonPanel({ refreshKey }) {
  const [stats, setStats] = useState([]);

  useEffect(() => {
    const load = () =>
      endpoints.aiModeStats().then((r) => setStats(r.data.modes || [])).catch(() => {});
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [refreshKey]);

  const nb = stats.find((m) => m.mode === "neurobro");
  const cl = stats.find((m) => m.mode === "claude");
  const bothHave = (nb?.trades ?? 0) > 0 && (cl?.trades ?? 0) > 0;
  const nbWins = bothHave && nb.total_pnl > cl.total_pnl;
  const clWins = bothHave && cl.total_pnl > nb.total_pnl;

  return (
    <div data-testid="ai-compare-panel" className="border border-border rounded-sm bg-[#0d0d0d] p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          Perbandingan AI · Neurobro vs Claude
        </h3>
        <span className="font-mono text-[10px] text-muted-foreground">
          dari trade tertutup (ai_mode)
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <SourceCard
          testid="ai-compare-neurobro"
          label="NEUROBRO AI"
          accent={{ text: "text-signal-buy", border: "border-signal-buy/40", bg: "bg-signal-buy/5" }}
          stat={nb}
          winner={nbWins}
        />
        <SourceCard
          testid="ai-compare-claude"
          label="CLAUDE AI"
          accent={{ text: "text-signal-brand", border: "border-signal-brand/40", bg: "bg-signal-brand/5" }}
          stat={cl}
          winner={clWins}
        />
      </div>
    </div>
  );
}
